import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { companyInfo } from "../mockData";
import { 
  Users, 
  Shield, 
  Target, 
  Award, 
  ArrowRight, 
  Mail, 
  Phone, 
  MapPin,
  Send,
  Scale,
  Heart,
  Building,
  Clock,
  CheckCircle,
  Star
} from "lucide-react";

const About = () => {
  const stats = [
    { number: "10,000+", label: "Customers Served", icon: Users },
    { number: "50+", label: "Document Types", icon: Scale },
    { number: "99%", label: "Customer Satisfaction", icon: Star },
    { number: "24/7", label: "Support Available", icon: Clock }
  ];

  const values = [
    {
      icon: Target,
      title: "Accessibility",
      description: "Making legal document preparation accessible to everyone, regardless of their legal knowledge or budget."
    },
    {
      icon: Shield,
      title: "Trust & Security",
      description: "Your personal information is protected with bank-level security and we never share your data."
    },
    {
      icon: Heart,
      title: "Customer-First",
      description: "Every feature and service is designed with our customers' needs and success in mind."
    },
    {
      icon: Award,
      title: "Quality Assurance",
      description: "All documents are regularly updated to ensure compliance with current state and federal laws."
    }
  ];

  const team = [
    {
      name: "Todd Cohen",
      role: "CEO & Co-Founder",
      bio: "Former attorney with 15+ years experience in family and business law. Passionate about making legal services accessible.",
      image: "https://images.unsplash.com/photo-1651684215020-f7a5b6610f23?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHx3aGl0ZSUyMG1hbGUlMjBleGVjdXRpdmUlMjBoZWFkc2hvdHMlMjBwcm9mZXNzaW9uYWx8ZW58MHx8fHwxNzU3MzYxMzQwfDA&ixlib=rb-4.1.0&q=85"
    },
    {
      name: "Michael Hartford",
      role: "CTO & Co-Founder",
      bio: "Technology leader with expertise in secure document processing and user experience design.",
      image: "https://images.unsplash.com/photo-1627161684850-52a7d958f8d7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHx3aGl0ZSUyMG1hbGUlMjBleGVjdXRpdmUlMjBoZWFkc2hvdHMlMjBwcm9mZXNzaW9uYWx8ZW58MHx8fHwxNzU3MzYxMzQwfDA&ixlib=rb-4.1.0&q=85"
    },
    {
      name: "Dora Katz",
      role: "Head of Legal Operations",
      bio: "Legal operations expert ensuring all forms meet state-specific requirements and legal standards.",
      image: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMHBvcnRyYWl0fGVufDB8fHx8MTc1NzM2MDI5Mnww&ixlib=rb-4.1.0&q=85"
    }
  ];

  const milestones = [
    { year: "2023", event: "FormForgePro founded with mission to simplify legal documents" },
    { year: "2023", event: "Launched with 10 essential legal document types" },
    { year: "2024", event: "Served 5,000+ customers across all 50 states" },
    { year: "2024", event: "Expanded to 50+ document types and premium services" },
    { year: "2026", event: "Reached 10,000+ satisfied customers milestone" }
  ];

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-blue-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Badge className="mb-6 bg-orange-100 text-orange-800 hover:bg-orange-200 px-4 py-2">
              About FormForgePro
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Making Legal Simple for Everyone
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              We believe that legal document preparation shouldn't be complicated or expensive. 
              Our mission is to empower individuals and businesses with the tools they need to handle their legal matters confidently.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                asChild
                size="lg"
                className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-4 rounded-lg text-lg"
              >
                <Link to="/services">
                  Explore Our Services
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button 
                asChild
                variant="outline"
                size="lg"
                className="border-2 border-blue-900 text-blue-900 hover:bg-blue-900 hover:text-white font-semibold px-8 py-4 rounded-lg text-lg"
              >
                <Link to="/help">
                  Get Support
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const IconComponent = stat.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <IconComponent className="h-8 w-8 text-orange-600" />
                  </div>
                  <div className="text-3xl font-bold text-slate-900 mb-2">
                    {stat.number}
                  </div>
                  <div className="text-gray-600">
                    {stat.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Our Story
              </h2>
              <div className="space-y-4 text-gray-600">
                <p>
                  FormForgePro was born from a simple observation: legal document preparation 
                  was unnecessarily complex and expensive for ordinary people. Our founders, 
                  with backgrounds in law and technology, saw an opportunity to bridge this gap.
                </p>
                <p>
                  After witnessing countless individuals struggle with legal paperwork or pay 
                  excessive attorney fees for simple document preparation, we set out to create 
                  a better solution. We wanted to build a platform that would guide users 
                  through the process step-by-step, using plain English instead of legal jargon.
                </p>
                <p>
                  Today, we're proud to have helped over {companyInfo.customersServed} individuals 
                  and businesses across all 50 states prepare their legal documents with confidence 
                  and at a fraction of traditional costs.
                </p>
              </div>
            </div>
            <div className="flex justify-center">
              <div className="relative w-96 h-96">
                <img 
                  src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=600&h=600&fit=crop"
                  alt="Our team working"
                  className="w-full h-full object-cover rounded-full border-8 border-teal-500 shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Our Values
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              These principles guide everything we do and every decision we make
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <Card key={index} className="text-center border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <CardContent className="p-6">
                    <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="h-8 w-8 text-teal-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-3">
                      {value.title}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Our Journey
            </h2>
            <p className="text-xl text-gray-600">
              Key milestones in our mission to simplify legal document preparation
            </p>
          </div>
          
          <div className="relative">
            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-orange-200"></div>
            <div className="space-y-8">
              {milestones.map((milestone, index) => (
                <div key={index} className="relative flex items-start">
                  <div className="flex-shrink-0 w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center relative z-10">
                    <CheckCircle className="h-4 w-4 text-white" />
                  </div>
                  <div className="ml-6">
                    <div className="flex items-center space-x-4 mb-2">
                      <Badge className="bg-orange-100 text-orange-800">
                        {milestone.year}
                      </Badge>
                    </div>
                    <p className="text-gray-700">
                      {milestone.event}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Meet Our Team
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              The passionate people behind FormForgePro who are dedicated to your success
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <Card key={index} className="text-center border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="relative w-24 h-24 mx-auto mb-4">
                    <img 
                      src={member.image}
                      alt={member.name}
                      className="w-24 h-24 rounded-full object-cover border-4 border-teal-500"
                    />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">
                    {member.name}
                  </h3>
                  <p className="text-orange-600 font-medium mb-3">
                    {member.role}
                  </p>
                  <p className="text-gray-600 text-sm">
                    {member.bio}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Get in Touch
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Have questions about our services or need personalized assistance? 
                We're here to help you succeed with your legal document needs.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                    <Mail className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Email Support</h3>
                    <p className="text-gray-600">{companyInfo.email}</p>
                  </div>
                </div>
                

              </div>
            </div>
            
            {/* Contact Form */}
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-slate-900 mb-6">
                  Send us a Message
                </h3>
                <form className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-900 mb-2">
                        First Name *
                      </label>
                      <Input
                        type="text"
                        required
                        placeholder="Enter your first name"
                        className="border-gray-200 focus:border-orange-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-900 mb-2">
                        Last Name *
                      </label>
                      <Input
                        type="text"
                        required
                        placeholder="Enter your last name"
                        className="border-gray-200 focus:border-orange-500"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-900 mb-2">
                      Email Address *
                    </label>
                    <Input
                      type="email"
                      required
                      placeholder="Enter your email address"
                      className="border-gray-200 focus:border-orange-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-900 mb-2">
                      Subject *
                    </label>
                    <Input
                      type="text"
                      required
                      placeholder="What can we help you with?"
                      className="border-gray-200 focus:border-orange-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-900 mb-2">
                      Message *
                    </label>
                    <Textarea
                      required
                      placeholder="Tell us more about your question or how we can help..."
                      rows={5}
                      className="border-gray-200 focus:border-orange-500"
                    />
                  </div>
                  
                  <Button 
                    type="submit"
                    size="lg"
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3"
                  >
                    <Send className="mr-2 h-5 w-5" />
                    Send Message
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Join thousands of satisfied customers who trust FormForgePro for their legal document needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild
              size="lg"
              className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-4 rounded-lg text-lg"
            >
              <Link to="/services">
                Browse Services
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button 
              asChild
              variant="outline"
              size="lg"
              className="border-2 border-white text-white hover:bg-white hover:text-blue-900 font-semibold px-8 py-4 rounded-lg text-lg"
            >
              <Link to="/pricing">
                View Pricing
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;